import React, { useEffect, useState } from "react";
import { Trash2, Mail, Phone, Calendar, CheckCircle2, XCircle, Check, RotateCcw, Bot, Download } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const notifyInbox = () => window.dispatchEvent(new Event("inbox-updated"));

// Cassie rates every lead she captures. Ring the hot ones first.
const QUALITY_RANK = { High: 0, Medium: 1, Low: 2 };
const rank = (e) => (e.lead_quality in QUALITY_RANK ? QUALITY_RANK[e.lead_quality] : 3);
const QUALITY_FILTERS = [
  { key: "all", label: "All" },
  { key: "High", label: "High intent" },
  { key: "Medium", label: "Medium" },
  { key: "Low", label: "Low" },
];

const csvCell = (v) => {
  const s = (v === null || v === undefined) ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportEnquiriesCsv = (items) => {
  const headers = ["Name", "Business", "Email", "Phone", "Website", "Services", "Software", "Preferred contact", "Lead quality", "Came from", "Message", "Source", "Dealt with", "Received"];
  const rows = items.map((e) => [
    e.name, e.business_name, e.email, e.phone, e.website_url,
    (e.services && e.services.length ? e.services.join("; ") : (e.service || "")),
    e.current_software, e.contact_method, e.lead_quality || "",
    e.attribution ? [e.attribution.source, e.attribution.medium, e.attribution.campaign].filter(Boolean).join(" / ") : "",
    (e.message || "").replace(/\n/g, " "),
    e.source || "Contact form", e.dealt_with ? "Yes" : "No", fmt(e.created_at),
  ]);
  const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cass-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function EnquiriesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [tab, setTab] = useState("inbox");
  const [quality, setQuality] = useState("all");

  const load = () => { setLoading(true); adminApi.enquiries.list().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleDelete = async () => {
    try { await adminApi.enquiries.remove(deleteId); toast({ title: "Enquiry deleted" }); load(); notifyInbox(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const toggleDealt = async (e) => {
    const next = !e.dealt_with;
    setItems((list) => list.map((it) => (it.id === e.id ? { ...it, dealt_with: next } : it)));
    try { await adminApi.enquiries.setDealt(e.id, next); notifyInbox(); toast({ title: next ? "Marked as dealt with — moved to Dealt with" : "Reopened — moved back to Inbox" }); }
    catch { toast({ title: "Couldn't update", variant: "destructive" }); load(); }
  };

  const outstanding = items.filter((e) => !e.dealt_with).length;
  const dealtCount = items.length - outstanding;
  const inTab = items.filter((e) => (tab === "dealt" ? e.dealt_with : !e.dealt_with));
  const qualityCounts = QUALITY_FILTERS.reduce((acc, q) => {
    acc[q.key] = q.key === "all" ? inTab.length : inTab.filter((e) => e.lead_quality === q.key).length;
    return acc;
  }, {});
  const rated = inTab.some((e) => e.lead_quality);
  const visible = inTab
    .filter((e) => quality === "all" || e.lead_quality === quality)
    // Highest intent first, then most recent — so the team works top-down.
    .sort((a, b) => rank(a) - rank(b) || String(b.created_at || "").localeCompare(String(a.created_at || "")));

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Contact Enquiries</h1>
          <p className="text-sm text-slate-500">{items.length} total • <span className="font-semibold text-orange">{outstanding} to deal with</span> — submissions from the website contact form</p>
        </div>
        <Button
          data-testid="export-enquiries-csv"
          onClick={() => exportEnquiriesCsv(items)}
          disabled={items.length === 0}
          variant="outline"
          className="rounded-full border-navy/20 text-navy hover:bg-navy hover:text-white shrink-0"
        >
          <Download size={15} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="mb-5 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="enquiries-tabs">
        <button
          data-testid="enquiries-tab-inbox"
          onClick={() => setTab("inbox")}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "inbox" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}
        >
          Inbox <span className={`ml-1 text-xs font-bold ${tab === "inbox" ? "text-orange" : "text-slate-400"}`}>{outstanding}</span>
        </button>
        <button
          data-testid="enquiries-tab-dealt"
          onClick={() => setTab("dealt")}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "dealt" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}
        >
          <Check size={14} /> Dealt with <span className={`text-xs font-bold ${tab === "dealt" ? "text-green-600" : "text-slate-400"}`}>{dealtCount}</span>
        </button>
      </div>

      {rated && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="enquiries-quality-filter">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400 mr-1">Lead quality</span>
          {QUALITY_FILTERS.map((q) => (
            <button
              key={q.key}
              data-testid={`enquiries-quality-${q.key.toLowerCase()}`}
              onClick={() => setQuality(q.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                quality === q.key ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-orange"
              }`}
            >
              {q.label} <span className={quality === q.key ? "text-white/70" : "text-slate-400"}>{qualityCounts[q.key]}</span>
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-1">Highest intent shown first</span>
        </div>
      )}

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">No enquiries yet.</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400" data-testid="enquiries-empty">
          {quality !== "all" ? `No ${quality.toLowerCase()}-quality leads in this view.` : tab === "dealt" ? "Nothing marked as dealt with yet." : "Inbox zero — no outstanding enquiries. 🎉"}
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((e) => (
            <div key={e.id} data-testid={`enquiry-${e.id}`} className={`rounded-xl border p-5 transition-colors ${e.dealt_with ? "bg-slate-50/70 border-slate-100" : "bg-white border-slate-100"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-navy">{e.name}{e.business_name ? <span className="font-normal text-slate-400"> · {e.business_name}</span> : ""}</p>
                    {e.source === "Cassie AI chatbot" && <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-sky-100 text-sky-700 flex items-center gap-1"><Bot size={12} /> Cassie</span>}
                    {e.lead_quality && <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${e.lead_quality === "High" ? "bg-green-100 text-green-700" : e.lead_quality === "Low" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>{e.lead_quality} intent</span>}
                    {e.dealt_with && <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1"><Check size={12} /> Dealt with</span>}
                    {(e.services && e.services.length > 0 ? e.services : (e.service ? [e.service] : [])).map((s, i) => (
                      <span key={i} className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-orange/10 text-orange">{s}</span>
                    ))}
                    {e.email_sent ? (
                      <span className="text-xs flex items-center gap-1 text-green-600"><CheckCircle2 size={13} /> emailed</span>
                    ) : (
                      <span className="text-xs flex items-center gap-1 text-amber-500"><XCircle size={13} /> not emailed</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
                    <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-orange"><Mail size={13} /> {e.email}</a>
                    {e.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {e.phone}</span>}
                    <span className="flex items-center gap-1.5"><Calendar size={13} /> {fmt(e.created_at)}</span>
                  </div>
                  {(e.business_name || e.website_url || e.current_software || e.contact_method) && (
                    <div className="flex items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500 flex-wrap">
                      {e.business_name && <span><span className="text-slate-400">Business:</span> <span className="font-medium text-navy">{e.business_name}</span></span>}
                      {e.website_url && <a href={e.website_url.startsWith("http") ? e.website_url : `https://${e.website_url}`} target="_blank" rel="noopener noreferrer" className="hover:text-orange"><span className="text-slate-400">Web:</span> {e.website_url}</a>}
                      {e.current_software && <span><span className="text-slate-400">Software:</span> <span className="font-medium text-navy">{e.current_software}</span></span>}
                      {e.contact_method && <span><span className="text-slate-400">Prefers:</span> <span className="font-medium text-navy">{e.contact_method}</span></span>}
                    </div>
                  )}
                  {e.attribution && (
                    <p className="mt-3 text-xs text-slate-500" data-testid={`enquiry-attribution-${e.id}`}>
                      <span className="text-slate-400">Came from:</span>{" "}
                      <span className="font-semibold text-navy capitalize">{e.attribution.source || "direct"}</span>
                      {e.attribution.medium ? ` (${e.attribution.medium})` : ""}
                      {e.attribution.campaign ? ` · ${e.attribution.campaign}` : ""}
                      {e.attribution.landing ? ` · landed on ${e.attribution.landing}` : ""}
                    </p>
                  )}
                  <p className="mt-3 text-slate-600 text-sm bg-slate-50 rounded-lg p-3 whitespace-pre-line">{e.message}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    data-testid={`enquiry-dealt-${e.id}`}
                    onClick={() => toggleDealt(e)}
                    title={e.dealt_with ? "Reopen" : "Mark as dealt with"}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${e.dealt_with ? "bg-slate-100 hover:bg-slate-200 text-slate-500" : "bg-green-50 hover:bg-green-100 text-green-600"}`}
                  >
                    {e.dealt_with ? <RotateCcw size={15} /> : <Check size={16} />}
                  </button>
                  <button onClick={() => setDeleteId(e.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
